import React, { useCallback, useEffect, useState, useRef } from 'react';
import FilterForPlanAction from '../../planAction/filterForPlanAction/FilterForPlanAction';
import { isEqual } from 'lodash';
import { filterConfigReportStatus, filtersOpenReportStatus, months, years } from './filterDataStatus';
import { StatusDone } from './StatusDone';
import { StatisticCard } from './StatisticCard';
import { ReportIcon } from 'src/components/ui-icons/sidebar';
import TableRow from './TableRow';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { useNavigate } from 'react-router-dom';
import { GroupItem, IFilters } from '../../../types/typesVPO';
import moment from 'moment';
import { selectCurrentUser } from 'src/modules/users/selector';
import { selectModelData } from '../../../store/filtersData/selector';
import { fetchData } from '../../../store/filtersData/actions';
import { transformDataForFilters } from '../../../utils/plan_actions/plan_actions';
import { fetchReportsRequest } from '../../../store/reports/actions';
import { selectReportsData, selectReportsLoading } from '../../../store/reports/selectors';

export default function ReportStatusMain() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => selectCurrentUser(state));
  const [currentFilters, setCurrentFilters] = useState<any>(null);
  const initialFiltersRef = useRef<any>(null);
  const lastRequestParamsRef = useRef<string>('');

  const FilterColumns = useAppSelector((state: any) => selectModelData(state, 'vpo-columns'))
    ?.slice()
    .sort((a, b) => a.SortNumber - b.SortNumber);
  const staticGroups = useAppSelector((state: any) => selectModelData(state, 'vpo-groups'));
  const factoryVPO = useAppSelector((state: any) => selectModelData(state, 'vpo-factories'));
  const VpoAvailableQuestionnaire = useAppSelector((state: any) => selectModelData(state, 'vpo-available-questionnaires')).sort((a, b)=>a.id - b.id);

  const reportsData = useAppSelector(selectReportsData);
  const reportsLoading = useAppSelector(selectReportsLoading);

  //! Получаю группы
  useEffect(() => {
    dispatch(
      fetchData(
        'vpo-groups',
        {},
        {
          page: 1,
          pageSize: 100,
        }
      )
    );
  }, [dispatch]);

  //! При изменении группы получаю колонны и заводы
  useEffect(() => {
    dispatch(fetchData('vpo-columns', {}, { page: 1, pageSize: 100 }));
    if (currentFilters?.groupReportPeriod?.selectedIds) {
      dispatch(fetchData('vpo-factories', { group: currentFilters?.groupReportPeriod?.selectedIds }, { page: 1, pageSize: 100 }));
    }
  }, [currentFilters?.groupReportPeriod?.selectedIds, dispatch]);

  //! получаю даты для фильтров по месяцу
  useEffect(() => {
    dispatch(fetchData('vpo-available-questionnaires', {}, { page: 1, pageSize: 100 }));
  }, [dispatch]);

  //! Выбор группы согласно групп доступа
  const getDefaultGroup = useCallback(() => {
    if (!user?.infoVPO) {
      return null;
    }
    if (user.infoVPO.zone || user.infoVPO.adminRegion) {
      if (user.infoVPO.adminRegion && user.infoVPO.directoryTitle?.length > 0) {
        const userGroupTitles = user.infoVPO.directoryTitle;
        const userGroups = staticGroups.filter((group) => userGroupTitles.includes(group.title));

        const sortedGroups = [...userGroups].sort((a, b) => {
          const getPriority = (group: GroupItem) => {
            if (group.title === 'BOP') return 1;
            if (group.title === 'VOP') return 2;
            return 3;
          };
          const priorityA = getPriority(a);
          const priorityB = getPriority(b);
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }
          return a.title.localeCompare(b.title);
        });
        return sortedGroups.length > 0 ? sortedGroups[0] : null;
      }
      return null;
    }
    const userGroupTitles = user.infoVPO.directoryTitle || [];
    const userGroups = staticGroups.filter((group) => userGroupTitles.includes(group.title));

    if (userGroups.length === 0 && user.infoVPO.factory?.length > 0) {
      const userFactoryIds = user.infoVPO.factory.map((f: { factoryID: number }) => f.factoryID);

      for (const factory of factoryVPO) {
        if (userFactoryIds.includes(factory.ID) && factory.VPOgroup?.[0]?.Title) {
          const factoryGroupTitle = factory.VPOgroup[0].title;
          const factoryGroup = staticGroups.find((g) => g.title === factoryGroupTitle);
          if (factoryGroup) {
            return factoryGroup;
          }
        }
      }
    }

    const sortedGroups = [...userGroups].sort((a, b) => {
      const getPriority = (group: GroupItem) => {
        if (group.Title === 'BOP') return 1;
        if (group.Title === 'VOP') return 2;
        return 3;
      };
      const priorityA = getPriority(a);
      const priorityB = getPriority(b);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return a.Title.localeCompare(b.Title);
    });
    return sortedGroups.length > 0 ? sortedGroups[0] : null;
  }, [user?.infoVPO, factoryVPO, staticGroups]);

  //! Определение месяца согласно из таблицы Vpo-available-questionnaire
  const getCurrentDate = (arrayEndQuestionary: any[]) => {
    const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

    const validItems = arrayEndQuestionary.filter((item) => item.startDate && moment(item.startDate).isValid());

    if (validItems.length === 0) {
      const firstItem = arrayEndQuestionary[0];
      const monthIndex = months.indexOf(firstItem?.month);
      return {
        month: {
          id: monthIndex + 1,
          name: firstItem?.month,
        },
        year: {
          id: Number(firstItem?.year),
          name: String(firstItem?.year),
        },
      };
    }

    const currentMoment = moment();
    const startedItems = validItems.filter((item) => moment(item.startDate) <= currentMoment);

    const currentDate = startedItems.length > 0
      ? startedItems[startedItems.length - 1]
      : validItems[0];

    const monthIndex = months.indexOf(currentDate?.month);

    return {
      month: {
        id: monthIndex + 1,
        name: currentDate?.month,
      },
      year: {
        id: Number(currentDate?.year),
        name: String(currentDate?.year),
      },
    };
  };

  //! начальное состояние фильтров
  useEffect(() => {
    if (VpoAvailableQuestionnaire.length && !initialFiltersRef.current && staticGroups.length && user?.infoVPO) {
      const currentDate = getCurrentDate(VpoAvailableQuestionnaire);
      const defaultGroup = getDefaultGroup();

      const initialFilter = {
        year: {
          selected: [currentDate.year.name],
          selectedIds: [currentDate.year.id],
          search: '',
        },
        month: {
          selected: [currentDate.month.name],
          selectedIds: [currentDate.month.id],
          search: '',
        },
        factoryReport: {
          selected: [],
          selectedIds: [],
          search: '',
        },
        groupReportPeriod: {
          selected: defaultGroup ? [defaultGroup.title] : [],
          selectedIds: defaultGroup ? [defaultGroup.id] : [],
          search: '',
        },
      };
      initialFiltersRef.current = initialFilter;
      setCurrentFilters(initialFilter);
    }
  }, [VpoAvailableQuestionnaire, staticGroups, user?.infoVPO, getDefaultGroup]);

  //! Загрузка данных отчета с страпи
  useEffect(() => {
    if (!currentFilters?.factoryReport?.selectedIds || !FilterColumns || factoryVPO.length === 0) return;
    const factories = currentFilters.factoryReport.selectedIds.length > 0
      ? currentFilters.factoryReport.selectedIds
      : factoryVPO.map((f: any) => f.id);
    const columns = FilterColumns.map((col: any) => col.id);
    const month = currentFilters?.month?.selectedIds?.[0];
    const year = currentFilters?.year?.selected?.[0];
    const group = currentFilters?.groupReportPeriod?.selectedIds?.[0];

    // Проверяем что параметры изменились
    const requestParams = JSON.stringify({ factories, columns, month, year, group });
    if (factories.length > 0 && columns.length > 0 && month && year && requestParams !== lastRequestParamsRef.current) {
      lastRequestParamsRef.current = requestParams;
      dispatch(fetchReportsRequest(factories, columns, month, year, undefined, group));
    }
  }, [dispatch, currentFilters?.factoryReport?.selectedIds, currentFilters?.month?.selectedIds, currentFilters?.year?.selected, currentFilters?.groupReportPeriod?.selectedIds, FilterColumns, factoryVPO]);

  const handleFiltersChange = useCallback(
    (newFilters: React.SetStateAction<IFilters>) => {
      if (!isEqual(currentFilters, newFilters)) {
        // console.log('Новые фильтры ОТЧЕТЫ:', newFilters);
        setCurrentFilters(newFilters);
      }
    },
    [currentFilters]
  );

  const transformedFactories = React.useMemo(() => transformDataForFilters(factoryVPO), [factoryVPO]);
  const transformedroups = React.useMemo(() => transformDataForFilters(staticGroups), [staticGroups]);

  const filterConfigs = {
    year: { items: years },
    month: { items: months },
    factoryReport: { items: transformedFactories },
    groupReportPeriod: { items: transformedroups },
  };

  const filtersMap = {
    year: years,
    month: months,
    factoryReport: transformedFactories,
    groupReportPeriod: transformedroups,
  };

  //! Фильтрация заводов согласно выбранным в фильтре
  const filteredFactories = React.useMemo(() => {
    if (!currentFilters?.factoryReport?.selectedIds?.length) {
      return factoryVPO;
    }
    return factoryVPO.filter((factory: any) =>
      currentFilters.factoryReport.selectedIds.includes(factory.id || factory.ID)
    );
  }, [factoryVPO, currentFilters?.factoryReport?.selectedIds]);

  if (!currentFilters) {
    return null;
  }

  return (
    <div className='flex justify-between'>
      <div className='w-[320px] mr-2 bg-white'>
        <FilterForPlanAction
          onFiltersChange={handleFiltersChange}
          filterConfigs={filterConfigs}
          filtersMap={filtersMap}
          initialFilters={initialFiltersRef.current || {}}
          filtersOpen={filtersOpenReportStatus}
          filtersConfig={filterConfigReportStatus}
          responsibleFil={false}
          selectedGropu={true}
          heightFilter={'h-[calc(100vh-2px)]'}
        />
      </div>
      <div className='flex-1 min-w-0 m-4'>
        <div className='flex items-center mb-6 font-normal text-primary'>
          <span className='pr-3'>
            <ReportIcon />
          </span>
          <span className='cursor-pointer' onClick={() => navigate('/dashboard/6/reports')}>
            Отчеты
          </span>
          <span className='mx-2'>→</span>
          <span>Отчет по закрытию периода самооценки</span>
        </div>
        <div className='mb-6  min-w-0'>
          <StatisticCard FilteGroup={filteredFactories} reportsData={reportsData} />
        </div>
        <div className='mb-6  min-w-0'>
          <StatusDone FilteGroup={filteredFactories} reportsData={reportsData} />
        </div>
        <div className='bg-white rounded-lg shadow min-w-0'>
          <TableRow
            FilteGroup={filteredFactories}
            FilterColumns={FilterColumns}
            reportsData={reportsData}
            reportsLoading={reportsLoading}
          />
        </div>
      </div>
    </div>
  );
}
